import type { DurationValue } from "components/ds/DurationField";
import type {
  MFAccessRequest,
  MFRequestableRole,
  MFRequestLine,
  MFRoleHolding,
  MFRoleTerms,
} from "./catalogs";
import { capDuration, exceedsCap } from "./durationCaps";

const plural = (unit: string, value: number) =>
  value === 1 ? unit : `${unit}s`;

const capLabel = (terms: Pick<MFRoleTerms, "maxTimeUnit" | "maxTimeValue">) =>
  terms.maxTimeUnit && typeof terms.maxTimeValue === "number"
    ? `${terms.maxTimeValue} ${plural(terms.maxTimeUnit, terms.maxTimeValue)}`
    : null;

// The duration a role is requested for when `requested` is asked of it: its
// terms are the role's and its side effects', so this is what the server
// accepts for it.
export const fitToRole = (
  requested: DurationValue,
  terms: MFRoleTerms,
): DurationValue => {
  if (terms.indefiniteOnly) return { ...requested, indefinite: true };
  if (terms.allowIndefinite) return requested;
  const cap = capDuration(terms.maxTimeValue, terms.maxTimeUnit);
  if (requested.indefinite) return cap ?? { ...requested, indefinite: false };
  return cap && exceedsCap(requested, cap) ? cap : requested;
};

// Said beside a role when the duration chosen for the whole request doesn't
// apply to it as it stands.
export const roleFitNote = (
  role: MFRequestableRole,
  requested: DurationValue,
): string | null => {
  const fitted = fitToRole(requested, role.terms);
  if (fitted.indefinite && !requested.indefinite)
    return `${role.name}: indefinitely`;
  if (
    fitted.num === requested.num &&
    fitted.unit === requested.unit &&
    Boolean(fitted.indefinite) === Boolean(requested.indefinite)
  )
    return null;
  const cap = capLabel(role.terms);
  return cap ? `${role.name}: up to ${cap}` : null;
};

export const roleTermsLabel = (terms: MFRoleTerms): string => {
  if (terms.indefiniteOnly) return "Indefinite only";
  if (terms.allowIndefinite) return "Any duration";
  const cap = capLabel(terms);
  return cap ? `Up to ${cap}` : "No duration allowed";
};

export const roleTermsSource = (role: MFRequestableRole): string | null =>
  role.knobSources?.duration?.name
    ? `via ${role.knobSources.duration.name}`
    : null;

const listNames = (names: string[]) =>
  names.length <= 1
    ? (names[0] ?? "")
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

// Who a request is for, as its copy names them: the requester, one other
// person or group by name, or several people at once.
export type RequestFor = "you" | "them" | { name: string };

const subjectOf = (requestFor: RequestFor) =>
  requestFor === "you" ? "you" : requestFor === "them" ? "they" : requestFor.name;

export const objectOf = (requestFor: RequestFor) =>
  requestFor === "you" ? "you" : requestFor === "them" ? "them" : requestFor.name;

export const holdingNote = (
  holding: MFRoleHolding | null,
  requestFor: RequestFor = "you",
): string | null => {
  if (!holding) return null;
  const holder = typeof requestFor === "object" ? requestFor.name : null;
  const already = holder ? `${holder} already has this` : "You already have this";
  switch (holding.kind) {
    case "through_group":
      return holding.groups.length
        ? `${already} through ${listNames(holding.groups.map((g) => g.name))}`
        : already;
    case "outside_ploy":
      return `${holder ? `${holder} has` : "You have"} this outside Ploy; this request takes it over and it is removed when the request ends`;
    default:
      return already;
  }
};

export const sideEffectSummary = (role: MFRequestableRole): string | null => {
  if (!role.via || role.consequences.length === 0) return null;
  const names = role.consequences.map((c) => c.name).join(", ");
  return `via ${role.via.name}: ${role.consequences.length} more · ${names}`;
};

export type GroupedLine<L extends MFRequestLine = MFRequestLine> = {
  line: L;
  sideEffects: L[];
};

// Requested lines in order, each with the side effects its route also gives. A
// side effect several requested lines cause is listed once, under the first.
export const groupLines = <L extends MFRequestLine>(
  lines: L[] | null | undefined,
): GroupedLine<L>[] => {
  if (!lines?.length) return [];
  const requested = lines.filter((l) => l.cause === "requested");
  const groups = requested.map((line) => ({ line, sideEffects: [] as L[] }));
  const byId = new Map(groups.map((g) => [g.line.id, g]));
  const orphans: GroupedLine<L>[] = [];

  lines
    .filter((l) => l.cause === "side_effect")
    .forEach((effect) => {
      const cause = (effect.sideEffectOf ?? [])
        .map((id) => byId.get(id))
        .find(Boolean);
      if (cause) cause.sideEffects.push(effect);
      else orphans.push({ line: effect, sideEffects: [] });
    });

  return [...groups, ...orphans];
};

export const LINE_STATUS_LABEL: Record<MFRequestLine["status"], string> = {
  pending: "Pending",
  provisioning: "Setting up",
  provisioned: "Granted",
  failed: "Failed",
  refused: "Refused",
  cancelled: "Cancelled",
  revoked: "Revoked",
  expired: "Expired",
};

export const LINE_STATUS_COLOR: Record<MFRequestLine["status"], string> = {
  pending: "orange",
  provisioning: "orange",
  provisioned: "green",
  failed: "red",
  refused: "red",
  cancelled: "gray",
  revoked: "red",
  expired: "gray",
};

// Why a request on this resource can't be sent yet with the roles ticked, or
// null when it can. A resource without roles never blocks.
export const roleSelectionProblem = (
  roles: MFRequestableRole[] | null,
  selected: MFRequestableRole[],
  requestFor: RequestFor = "you",
): string | null => {
  if (!roles) return null;
  if (roles.length === 0) return noRolesNote(requestFor);
  if (selected.length === 0) return chooseRoleNote(requestFor);
  return null;
};

export const noRolesNote = (requestFor: RequestFor = "you") =>
  `There are no roles of this resource ${subjectOf(requestFor)} can request.`;

export const chooseRoleNote = (requestFor: RequestFor = "you") =>
  requestFor === "you"
    ? "Choose at least one role you need."
    : requestFor === "them"
      ? "Choose at least one role."
      : `Choose at least one role for ${requestFor.name}.`;

const IN_PROGRESS_REQUEST_STATUSES: MFAccessRequest["status"][] = [
  "pending",
  "approved",
  "provisioning",
];

export const isRequestInProgress = (
  status: MFAccessRequest["status"],
): boolean => IN_PROGRESS_REQUEST_STATUSES.includes(status);

const SETTLING_LINE_STATUSES: MFRequestLine["status"][] = [
  "pending",
  "provisioning",
];

// A role request reads "provisioned" as soon as one role lands, so it is still
// on its way while any of its lines is.
export const isRequestSettling = (
  request: Pick<MFAccessRequest, "status" | "lines"> | null | undefined,
): boolean => {
  if (!request) return false;
  return (
    isRequestInProgress(request.status) ||
    request.status === "deprovisioning" ||
    (request.lines ?? []).some((l) => SETTLING_LINE_STATUSES.includes(l.status))
  );
};
